/* eslint-disable */
import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import logoRectImg from 'src/assets/images/logo-rectangle.png';
import useTheme from 'src/hooks/useTheme';
import { useMarket } from 'src/context/marketContext';
import './index.scss';
import ThemeSwitch from './ThemeSwitch';

function Header() {
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {
    market, setMarket, mktData, markets, ui,
    lang, setLang, languages, version, setVersion,
  } = useMarket();
  const [marketOpen, setMarketOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const marketRef = useRef(null);
  const langRef = useRef(null);
  const versionRef = useRef(null);

  const versions = {
    realmoney: { key: 'realmoney', label: 'Real Money', flag: '\uD83D\uDCB0' },
    sweepstakes: { key: 'sweepstakes', label: 'Sweepstakes', flag: '\uD83C\uDF81' },
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (marketRef.current && !marketRef.current.contains(e.target)) setMarketOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (versionRef.current && !versionRef.current.contains(e.target)) setVersionOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { id: 'games', label: ui.navGames },
    { id: 'about', label: ui.navAbout },
    { id: 'contact', label: ui.navContact },
  ];

  return (
    <>
      <Helmet>
        <title>Rectangle Games</title>
        <link rel="icon" type="image/png" href="./favicon.png" />
      </Helmet>
      <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
        <div className="site-header__inner">
          <a href={window?.location?.href} className="site-header__logo">
            <img src={logoRectImg} alt="Rectangle Games" className={`logo ${isDarkTheme ? 'invert' : ''}`} />
          </a>

          {/* Market selector */}
          <div className="market-selector notranslate" ref={marketRef}>
            <button type="button" className="market-selector__trigger" onClick={() => setMarketOpen(!marketOpen)}>
              <span className="market-selector__flag">{mktData.flag}</span>
              <span className="market-selector__label">{mktData.label}</span>
              <svg className={`market-selector__chevron ${marketOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {marketOpen && (
              <div className="market-selector__dropdown">
                {Object.values(markets).map((m) => (
                  <button type="button" key={m.key} className={`market-selector__item ${market === m.key ? 'active' : ''}`} onClick={() => { setMarket(m.key); setMarketOpen(false); }}>
                    <span className="market-selector__item-flag">{m.flag}</span>
                    <span>{m.label}</span>
                    {market === m.key && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="market-selector__check">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Language selector */}
          <div className="market-selector notranslate" ref={langRef}>
            <button type="button" className="market-selector__trigger" onClick={() => setLangOpen(!langOpen)}>
              <span className="market-selector__flag">{languages[lang]?.flag}</span>
              <span className="market-selector__label">{languages[lang]?.label}</span>
              <svg className={`market-selector__chevron ${langOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {langOpen && (
              <div className="market-selector__dropdown">
                {Object.values(languages).map((l) => (
                  <button type="button" key={l.key} className={`market-selector__item ${lang === l.key ? 'active' : ''}`} onClick={() => { setLang(l.key); setLangOpen(false); }}>
                    <span className="market-selector__item-flag">{l.flag}</span>
                    <span>{l.label}</span>
                    {lang === l.key && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="market-selector__check">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Version selector */}
          <div className="market-selector notranslate" ref={versionRef}>
            <button type="button" className="market-selector__trigger" onClick={() => setVersionOpen(!versionOpen)}>
              <span className="market-selector__flag">{versions[version]?.flag}</span>
              <span className="market-selector__label">{versions[version]?.label}</span>
              <svg className={`market-selector__chevron ${versionOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {versionOpen && (
              <div className="market-selector__dropdown">
                {Object.values(versions).map((v) => (
                  <button type="button" key={v.key} className={`market-selector__item ${version === v.key ? 'active' : ''}`} onClick={() => { setVersion(v.key); setVersionOpen(false); }}>
                    <span className="market-selector__item-flag">{v.flag}</span>
                    <span>{v.label}</span>
                    {version === v.key && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="market-selector__check">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop nav */}
          <nav className="site-header__nav notranslate">
            {navLinks.map((link) => (
              <button type="button" key={link.id} className="nav-link" onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            ))}
          </nav>

          <div className="site-header__right">
            <div className="site-header__controls">
              <ThemeSwitch />
            </div>
            <button type="button" className="site-header__cta" onClick={() => window.dispatchEvent(new Event('open-contact'))}>
              {ui.getInTouch}
            </button>
            <button type="button" className={`mobile-toggle ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
              <span /><span /><span />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="site-header__mobile">
            {navLinks.map((link) => (
              <button type="button" key={link.id} className="mobile-nav-link" onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            ))}
            <button type="button" className="mobile-cta" onClick={() => { window.dispatchEvent(new Event('open-contact')); setMobileMenuOpen(false); }}>
              {ui.getInTouch}
            </button>
            <div className="mobile-controls-row">
              <ThemeSwitch />
            </div>
          </div>
        )}
      </header>
    </>
  );
}

export default Header;
